import {
  AntdTreeType,
  CategoryInterface,
  NomenclatureGroupInterface,
} from '@/types'
import {
  getApprovalStatusLabel,
  isApprovedStatus,
} from '@/utils/catalog/approvalStatus'

type convertToAntdFormatTreeParamsType = {
  nodes: NomenclatureGroupInterface[] | CategoryInterface[]
  classNames: Record<string, string>
  allSelectable: boolean
  inTreeSelect?: boolean
  inTree?: boolean
  respectApprovalStatus?: boolean
}

export default function convertToAntdFormatTree(
  convertToAntdFormatTreeParams: convertToAntdFormatTreeParamsType
) {
  const {
    nodes,
    classNames,
    allSelectable,
    inTreeSelect,
    inTree,
    respectApprovalStatus = true,
  } = convertToAntdFormatTreeParams

  function converter(
    nodes: NomenclatureGroupInterface[] | CategoryInterface[]
  ): AntdTreeType[] {
    const renderCategoryTitle = (
      description: string,
      status?: string | null
    ) => {
      const statusLabel = getApprovalStatusLabel(status)
      const statusClassName =
        status === 'REJECTED'
          ? 'approvalTreeNodeStatusRejected'
          : 'approvalTreeNodeStatusDraft'

      return (
        <span
          className={[
            classNames.treeNode,
            'approvalTreeNode',
            statusLabel ? 'approvalTreeNodeWithStatus' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span className="approvalTreeNodeLabel">{description}</span>
          {statusLabel && (
            <span
              className={['approvalTreeNodeStatus', statusClassName].join(' ')}
            >
              <span className="approvalTreeNodeStatusDot" />
              <span className="approvalTreeNodeStatusText">{statusLabel}</span>
            </span>
          )}
        </span>
      )
    }

    if ('category' in nodes[0] && inTree) {
      const newNodes = nodes as CategoryInterface[]
      const sortedNodes = [...newNodes].sort((a, b) =>
        a.category.description.localeCompare(b.category.description, 'ru')
      )
      return sortedNodes.map((node) => ({
        title: renderCategoryTitle(
          node.category.description,
          node.category.status
        ),
        filterProp: node.category.description,
        key: node.category.id,
        children:
          node.childCategories.length > 0
            ? converter(node.childCategories)
            : [],
        // isLeaf: !node.childCategories.length,
        checkable: !node.childCategories.length,
        disableCheckbox: node.childCategories.length > 0,
        selectable: false,
      }))
    }
    if ('nomenclatureGroup' in nodes[0] && inTree) {
      const newNodes = nodes as NomenclatureGroupInterface[]
      const sortedNodes = [...newNodes].sort((a, b) =>
        a.nomenclatureGroup.description.localeCompare(
          b.nomenclatureGroup.description,
          'ru'
        )
      )
      return sortedNodes.map((node) => ({
        title: node.nomenclatureGroup.description,
        key: node.nomenclatureGroup.id,
        children:
          node.childGroups.length > 0 ? converter(node.childGroups) : [],
        filterProp: node.nomenclatureGroup.description,
        // isLeaf: !node.childGroups.length,
        checkable: !node.childGroups.length,
        disableCheckbox: node.childGroups.length > 0,
        selectable: false,
      }))
    }
    if ('category' in nodes[0] && inTreeSelect) {
      const newNodes = nodes as CategoryInterface[]
      const sortedNodes = [...newNodes].sort((a, b) =>
        a.category.description.localeCompare(b.category.description, 'ru')
      )
      if (inTreeSelect) {
        return sortedNodes.map((node: CategoryInterface) => {
          const canSelectByTreeRule = !allSelectable
            ? node.childCategories.length == 0
            : allSelectable
          const isApproved = isApprovedStatus(node.category.status)
          const canSelect = respectApprovalStatus
            ? canSelectByTreeRule && isApproved
            : canSelectByTreeRule

          return {
            value: node.category.id,
            filterProp: node.category.description,
            key: node.category.id,
            title: renderCategoryTitle(
              node.category.description,
              node.category.status
            ),
            selectable: canSelect,
            disabled: respectApprovalStatus ? !isApproved : false,
            children:
              node.childCategories.length > 0
                ? converter(node.childCategories)
                : [],
          }
        })
      } else {
        return sortedNodes.map((node: CategoryInterface) => ({
          key: node.childCategories.length
            ? `${node.category.id} ${node.childCategories
                .map((childCategory) => childCategory.category.id)
                .join('')
                .split('')
                .sort(() => Math.random() - 0.5)
                .join('')}`
            : `${node.category.id} ${node.category.parentId
                .split('')
                .sort(() => Math.random() - 0.5)
                .join('')}`,
          title: (
            <span className={classNames.treeNode}>
              {node.category.description}
            </span>
          ),
          selectable: !allSelectable
            ? node.childCategories.length == 0
            : allSelectable,
          isLeaf: node.childCategories.length == 0,
          children:
            node.childCategories.length > 0
              ? converter(node.childCategories)
              : [],
        }))
      }
    }
    if ('nomenclatureGroup' in nodes[0] && inTreeSelect) {
      const newNodes = [...(nodes as NomenclatureGroupInterface[])]
      const sortedNodes = [...newNodes].sort((a, b) =>
        a.nomenclatureGroup.description.localeCompare(
          b.nomenclatureGroup.description,
          'ru'
        )
      )
      return sortedNodes.map((node: NomenclatureGroupInterface) => ({
        value: node.nomenclatureGroup.id,
        title: (
          <span className={classNames.treeNode}>
            {node.nomenclatureGroup.description}
          </span>
        ),
        key: node.nomenclatureGroup.id,
        filterProp: node.nomenclatureGroup.description,
        selectable: !allSelectable
          ? node.childGroups.length == 0
          : allSelectable,
        children:
          node.childGroups.length > 0 ? converter(node.childGroups) : [],
      }))
    }

    return []
  }

  return converter(nodes)
}
